import { css, cx } from "@emotion/css";
import { ActionButton, ActionButtonType, Button, Drawer } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { KubernetesLiveApplicationStatusCode, KubernetesLiveStatusDetailedResource, KubernetesLiveStatusResource, TaskState, TimelineItem } from "@octopusdeploy/octopus-server-client";
import { isPodResource } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useMemo, useState } from "react";
import type { ViewKubernetesObjectDrawerEvent } from "~/analytics/Analytics";
import { useAnalyticViewKubernetesObjectDrawerDispatch } from "~/analytics/Analytics";
import { EventsTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/EventsTab";
import { LogsTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/LogsTab";
import { ManifestTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/ManifestTab";
import { SummaryTab } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/SummaryTab";
import { flattenResourceHierarchy } from "~/areas/projects/components/Observability/ObjectDrawer/flattenResourceHierarchy";
import { ControlledTabsContainer, TabItem } from "~/primitiveComponents/navigation/Tabs/index";
type PartialViewKubernetesObjectDrawerEvent = {
    releaseId: string;
    deploymentStatus: TaskState;
    liveApplicationHealth: KubernetesLiveApplicationStatusCode;
    executionType: "deployment";
};
const AllTabs = ["Summary", "Logs", "Events", "Manifest"];
type TabsTuple = typeof AllTabs;
type Tabs = TabsTuple[number];
const styles = {
    mainContainer: css({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        position: "relative",
    }),
    tabItemContainer: css({
        padding: space[16],
        paddingBottom: space[32],
        height: "100%",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        overflowY: "auto",
    }),
    summaryLogsEventsTabItemContainer: css({
        //the logs & events containers needs to handle their own left and right padding for the line hover
        paddingLeft: 0,
        paddingRight: 0,
    }),
};
type LiveStatusObjectDrawerInnerProps = {
    environmentId: string;
    projectId: string;
    tenantId?: string;
    resource: KubernetesLiveStatusResource;
    latestDeployment: TimelineItem;
    analyticEventData: PartialViewKubernetesObjectDrawerEvent;
    onDetailedResourceLoaded: (detailedResource: KubernetesLiveStatusDetailedResource) => void;
};
const LiveStatusObjectDrawerInner = (props: LiveStatusObjectDrawerInnerProps) => {
    const { resource, projectId, environmentId, tenantId, latestDeployment, analyticEventData, onDetailedResourceLoaded } = props;
    const [activeTab, setActiveTab] = useState<Tabs>("Summary");
    const [hasViewedManifestTab, setHasViewedManifestTab] = useState(false);
    const { result, isLoading, error: detailedResourceError, } = useInlineStatusQuery(async (repo) => {
        const id = resource.DesiredResourceId || resource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        const response = await repo.KubernetesLiveStatus.getResource(projectId, environmentId, tenantId, resource.ResourceSourceId, resource.SourceType, id);
        onDetailedResourceLoaded(response.Resource);
        return response;
    }, [environmentId, onDetailedResourceLoaded, projectId, resource.DesiredResourceId, resource.ResourceId, resource.ResourceSourceId, resource.SourceType, tenantId], "Get live status resource details");
    const detailedResource = result?.Resource;
    const { result: manifest, isLoading: isLoadingManifest, error: manifestError, } = useInlineStatusQuery((repo) => {
        //if we haven't viewed the manifest tab yet, don't do anything
        if (!hasViewedManifestTab) {
            return Promise.resolve(null);
        }
        const id = resource.DesiredResourceId || resource.ResourceId;
        if (!id) {
            return Promise.reject("The live resource does not have a DesiredResourceId or ResourceId");
        }
        return repo.KubernetesLiveStatus.getResourceManifest(projectId, environmentId, tenantId, resource.ResourceSourceId, resource.SourceType, id);
    }, [environmentId, projectId, resource, tenantId, hasViewedManifestTab], "Get live status resource manifest");
    const showLogsTab = useMemo(() => {
        if (!detailedResource) {
            return false;
        }
        if (detailedResource.SourceType === "ArgoCDInstance") {
            return false;
        }
        const allResources = flattenResourceHierarchy(detailedResource);
        //we show the Logs tab if any resource in the hierarchy is a Pod resource
        return allResources?.some((r) => r && isPodResource(r)) || false;
    }, [detailedResource]);
    const onManifestTabViewed = () => {
        if (!hasViewedManifestTab) {
            setHasViewedManifestTab(true);
        }
    };
    const manifestAnalyticEventData = detailedResource &&
        resource && {
        ...analyticEventData,
        projectId,
        environmentId,
        deploymentId: latestDeployment.Id,
        objectHealth: detailedResource.HealthStatus,
        objectSyncStatus: detailedResource.SyncStatus,
        tab: activeTab,
        objectId: resource.ResourceId || "unknown",
        objectType: resource.Kind,
    };
    return (<div className={styles.mainContainer}>
            {detailedResource && (<LiveStatusObjectDrawerAnalyticView eventData={{
                ...analyticEventData,
                projectId: projectId,
                environmentId: environmentId,
                deploymentId: latestDeployment.Id,
                objectHealth: detailedResource.HealthStatus,
                objectSyncStatus: detailedResource.SyncStatus,
                tab: activeTab,
                objectId: resource.ResourceId || "unknown",
                objectType: resource.Kind,
            }}/>)}
            <ControlledTabsContainer value={activeTab} onChange={(tab) => setActiveTab(tab)}>
                <TabItem value={"Summary"} label={"Summary"}>
                    <div className={cx(styles.tabItemContainer, styles.summaryLogsEventsTabItemContainer)}>
                        <SummaryTab detailedResource={detailedResource} isLoading={isLoading} error={detailedResourceError}/>
                    </div>
                </TabItem>
                <TabItem label={"Events"} value={"Events"}>
                    <div className={cx(styles.tabItemContainer, styles.summaryLogsEventsTabItemContainer)}>
                        <EventsTab detailedResource={detailedResource} projectId={props.projectId} environmentId={props.environmentId} tenantId={props.tenantId}/>
                    </div>
                </TabItem>
                {showLogsTab && (<TabItem label={"Logs"} value={"Logs"}>
                        <div className={cx(styles.tabItemContainer, styles.summaryLogsEventsTabItemContainer)}>
                            <LogsTab detailedResource={detailedResource} projectId={props.projectId} environmentId={props.environmentId} tenantId={props.tenantId}/>
                        </div>
                    </TabItem>)}
                <TabItem label={"Manifest"} value={"Manifest"} onActive={onManifestTabViewed}>
                    <div className={styles.tabItemContainer}>
                        {manifestAnalyticEventData && (<ManifestTab detailedResource={detailedResource} liveManifest={manifest?.LiveManifest ?? null} desiredManifest={manifest?.DesiredManifest ?? null} isLoading={isLoadingManifest} error={manifestError} diff={manifest?.Diff ?? null} latestDeployment={latestDeployment} analyticEventData={manifestAnalyticEventData}/>)}
                    </div>
                </TabItem>
            </ControlledTabsContainer>
        </div>);
};
type LiveStatusObjectDrawerProps = Omit<LiveStatusObjectDrawerInnerProps, "resource" | "onDetailedResourceLoaded"> & {
    onClose: () => void;
    //we do this omit then re-add here so we can enforce the non-nullability on the inner component
    resource: KubernetesLiveStatusResource | undefined;
};
export const LiveStatusObjectDrawer = (props: LiveStatusObjectDrawerProps) => {
    const { onClose, resource, ...rest } = props;
    const [detailedResource, setDetailedResource] = useState<KubernetesLiveStatusDetailedResource | undefined>(undefined);
    const customActions = useMemo(() => {
        let actions = [<ActionButton key={`Cancel ${resource?.Name}`} type={ActionButtonType.Secondary} label="Close" accessibleName={`Close ${resource?.Name}}`} onClick={onClose}/>];
        if (detailedResource?.ExternalLink) {
            actions = [<Button importance={"secondary"} href={detailedResource.ExternalLink.Uri} label={detailedResource.ExternalLink.Label} opensInNewTab external/>, ...actions];
        }
        return actions;
    }, [detailedResource, onClose, resource]);
    const handleOnClose = () => {
        onClose();
        setDetailedResource(undefined);
    };
    const namespace = resource?.Namespace ? ` in ${resource.Namespace}` : "";
    return (<Drawer variant={"customActions"} isOpen={!!resource} onClose={handleOnClose} customActions={customActions} title={resource?.Name || ""} subtitle={`${resource?.Kind}${namespace}`} width={"wide"}>
            {resource && <LiveStatusObjectDrawerInner resource={resource} {...rest} onDetailedResourceLoaded={setDetailedResource}/>}
        </Drawer>);
};
function LiveStatusObjectDrawerAnalyticView({ eventData }: {
    eventData: ViewKubernetesObjectDrawerEvent;
}) {
    const dispatch = useAnalyticViewKubernetesObjectDrawerDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch("View Kubernetes Object Tab", eventData), [eventData.tab]);
    return <></>;
}
